import { useContext, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

import { ConfigContext } from '../../../aurr/contexts/ConfigContext';
import LoginContext from '../../../aurr/contexts/LoginContext';
import type { ApiRequestProps } from '../../../common/interface';
import type { Collection } from '../../../common/type';
import { COLLECTION_LIST_QUERY_KEY } from '../constants';

export interface CollectionListRequestParams extends ApiRequestProps {
  userId?: string;
  pathname?: string;
  provider?: string;
  designation?: string;
}

export const useCollectionsList = (params: CollectionListRequestParams) => {
  const {
    access_token: accessToken,
    isLoggedIn,
    userProfile,
  } = useContext(LoginContext);
  const { COMMUNITY_FUNCTIONS_URL } = useContext(ConfigContext);
  const { baseURL, userId, pathname, enabled = true, ...restParams } = params;
  const [isInitialLoaded, setInitialLoaded] = useState(false);
  const parsedBaseURL = baseURL || COMMUNITY_FUNCTIONS_URL;
  // Determine the endpoint based on login state
  let endpoint = isLoggedIn ? 'mycollections' : 'collections';
  if (userId && userId !== userProfile?.sub) {
    endpoint = 'collections';
  }

  const axiosConfig = {
    baseURL: `${parsedBaseURL}/store/v1/${endpoint}`,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  };
  const api = axios.create(axiosConfig);

  return useQuery<Collection[]>({
    queryKey: [COLLECTION_LIST_QUERY_KEY, parsedBaseURL, userId],
    queryFn: async () => {
      const response = await api
        .get('', {
          params: {
            userId,
            ...restParams,
          },
        })
        .catch(reason => {
          // eslint-disable-next-line no-console
          console.error('Error fetching Collections data: ', reason);
          return reason.response;
        });
      if (!isInitialLoaded) {
        setInitialLoaded(true);
      }
      return response?.data;
    },
    enabled,
  });
};
